import API_BASE from '../../utils/api';
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import ProfileDropdown from "../../components/ProfileDropdown";

const PrincipalLeaves = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    fetchLeaves();
  }, [filter]);

  const fetchLeaves = async () => {
    try {
      let url = `${API_BASE}/leaves`;
      if (filter === "pending") {
        url = `${API_BASE}/leaves/pending`;
      } else {
        url = `${API_BASE}/leaves?status=${filter}`;
      }

      const response = await axios.get(url);
      setLeaves(response.data);
    } catch (error) {
      console.error("Error fetching leaves:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      await axios.put(`${API_BASE}/leaves/${leaveId}/status`, {
        status: "approved",
        remarks,
      });
      setShowModal(false);
      setSelectedLeave(null);
      setRemarks("");
      fetchLeaves();
    } catch (error) {
      console.error("Error approving leave:", error);
      alert("Error approving leave");
    }
  };

  const handleReject = async (leaveId) => {
    try {
      await axios.put(`${API_BASE}/leaves/${leaveId}/status`, {
        status: "rejected",
        remarks,
      });
      setShowModal(false);
      setSelectedLeave(null);
      setRemarks("");
      fetchLeaves();
    } catch (error) {
      console.error("Error rejecting leave:", error);
      alert("Error rejecting leave");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getLeaveTypeIcon = (type) => {
    const icons = {
      sick: "🤒",
      casual: "😊",
      earned: "⭐",
      other: "📝",
    };
    return icons[type] || "📅";
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 bg-gray-100 flex items-center justify-center h-screen">
          <div className="text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 bg-gray-100 min-h-screen">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">
              Leave Management
            </h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex space-x-4">
              <button
                onClick={() => setFilter("pending")}
                className={`px-4 py-2 rounded-md ${
                  filter === "pending"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setFilter("approved")}
                className={`px-4 py-2 rounded-md ${
                  filter === "approved"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Approved
              </button>
              <button
                onClick={() => setFilter("rejected")}
                className={`px-4 py-2 rounded-md ${
                  filter === "rejected"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Rejected
              </button>
              <button
                onClick={() => setFilter("all")}
                className={`px-4 py-2 rounded-md ${
                  filter === "all"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All
              </button>
            </div>
          </div>

          {/* Leaves List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">
                {filter === "pending"
                  ? "Pending Leave Requests"
                  : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Leaves`}
              </h2>
            </div>

            {leaves.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <span className="text-6xl mb-4 block">📭</span>
                <p className="text-lg">No leave requests found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {leaves.map((leave) => (
                  <div key={leave._id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-2xl">
                            {getLeaveTypeIcon(leave.leaveType)}
                          </span>
                          <div>
                            <h3 className="font-semibold text-lg">
                              {leave.teacherId?.name || "Unknown Teacher"}
                            </h3>
                            <p className="text-sm text-gray-500">
                              Employee ID:{" "}
                              {leave.teacherId?.employeeId || "N/A"}
                            </p>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(leave.status)}`}
                          >
                            {leave.status}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div>
                            <p className="text-sm text-gray-500">Leave Type</p>
                            <p className="font-medium capitalize">
                              {leave.leaveType}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Duration</p>
                            <p className="font-medium">
                              {new Date(leave.startDate).toLocaleDateString()} -{" "}
                              {new Date(leave.endDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Days</p>
                            <p className="font-medium">
                              {Math.ceil(
                                (new Date(leave.endDate) -
                                  new Date(leave.startDate)) /
                                  (1000 * 60 * 60 * 24),
                              ) + 1}{" "}
                              days
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Applied On</p>
                            <p className="font-medium">
                              {new Date(leave.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4">
                          <p className="text-sm text-gray-500">Reason</p>
                          <p className="text-gray-700 bg-gray-50 p-3 rounded mt-1">
                            {leave.reason}
                          </p>
                        </div>

                        {leave.remarks && (
                          <div className="mt-4">
                            <p className="text-sm text-gray-500">Remarks</p>
                            <p className="text-gray-700 bg-gray-50 p-3 rounded mt-1">
                              {leave.remarks}
                            </p>
                          </div>
                        )}
                      </div>

                      {leave.status === "pending" && (
                        <div className="ml-4 flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedLeave(leave);
                              setShowModal(true);
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                          >
                            Review
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {showModal && selectedLeave && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-semibold">Review Leave Request</h3>
              <p className="text-sm text-gray-500">
                {selectedLeave.teacherId?.name} - {selectedLeave.leaveType}{" "}
                leave
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remarks (Optional)
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows="3"
                className="w-full border rounded-md px-3 py-2"
                placeholder="Add any remarks..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedLeave(null);
                  setRemarks("");
                }}
                className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReject(selectedLeave._id)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Reject
              </button>
              <button
                onClick={() => handleApprove(selectedLeave._id)}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrincipalLeaves;
